import React from 'react';
import { useTEF } from 'src/tef/components/providers/TEFProvider';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import type { ForwardedHTMLProps } from 'src/tef/components/utils/types';

interface SectionHeadingProps extends React.HTMLAttributes<HTMLHeadingElement>, ForwardedHTMLProps {
  children: React.ReactNode;
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  className?: string;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

export const SectionHeading = React.forwardRef<HTMLHeadingElement, SectionHeadingProps>(
  ({ children, level = 3, className, as, ...props }, ref) => {
    const tef = useTEF();
    const forwardedProps = forwardProps(props);

    const headingLevel = as || (`h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6');
    const headingStyle = tef.typography.heading[headingLevel];
    const sectionHeadingStyle = tef.typography.sectionHeading;

    const Component = headingLevel as React.ElementType;

    return (
      <Component
        ref={ref}
        className={cn(
          sectionHeadingStyle.size,
          sectionHeadingStyle.weight,
          sectionHeadingStyle.spacing,
          headingStyle.lineHeight,
          tef.colors.foreground,
          className
        )}
        {...forwardedProps}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

SectionHeading.displayName = 'SectionHeading';
